import {Component, HostListener, OnInit} from '@angular/core';
import * as moment from 'moment';
import {AlertController, ModalController} from '@ionic/angular';
import {ActivatedRoute, Router} from '@angular/router';
import {RepositoryService} from '../../../smoothr-web-app-core/services/repository/repository.service';
import {HomePage} from '../home/home.page';
import {Api} from '../../../smoothr-web-app-core/api/api';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import {PreorderType} from '../../../smoothr-web-app-core/enums/PreorderType';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateService} from '@ngx-translate/core';
import {OrderUtils} from '../../../smoothr-web-app-core/utils/order-utils';
import {environment} from '../../../environments/environment';
import {AnalyticsService} from '../../../smoothr-web-app-core/services/analytics/analytics.service';
import {firstOfObservable, numberD, numberToCurrency, sleep} from '../../../smoothr-web-app-core/utils/utils';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from '../../../assets/pdfmake/vfs_fonts/vfs_fonts';
import {TDocumentDefinitions} from 'pdfmake/interfaces';
import {BehaviorSubject, Subject, throwError, timer} from 'rxjs';
import {catchError, switchMap, takeUntil, takeWhile, tap} from 'rxjs/operators';
import {RequestEmailComponent} from './request-email/request-email.component';
import {PaymentStatus} from 'src/smoothr-web-app-core/models/PaymentStatus';
import Payment from 'src/smoothr-web-app-core/models/Payment';
import {MenuPage} from '../menu/menu.page';

pdfMake.vfs = pdfFonts;

@Component({
	selector: 'app-payment-success',
	templateUrl: './payment-success.page.html',
	styleUrls: ['payment-success.page.scss']
})
export class PaymentSuccessPage extends RepositoryDirective implements OnInit {
	static url = 'payment/success';
	static urlWithPaymentParam = 'payment/success/:payment';
	static ORDER_ID_PARAM = 'order';
	private destroy$ = new Subject<void>();

	moment = moment;
	OrderUtils = OrderUtils;
	pt = PreorderType;
	orderVenue: Venue;
	error: any;
	orderId: string;
	environment = environment;
	numberToCurrency = numberToCurrency;
	loading = true;
	tip: number = 0;
	private vrPaymentConfirmed = false;
	private beaconSent = false;

	constructor(
		private translate: TranslateService,
		protected repository: RepositoryService,
		private alertCtrl: AlertController,
		private router: Router,
		private snackbarCtrl: MatSnackBar,
		private route: ActivatedRoute,
		private analytics: AnalyticsService,
		private modalCtrl: ModalController
	) {
		super(repository);
	}

	static async navigate(router: Router, order: string[], payment: string) {
		await router.navigate([this.url + '/' + payment], {
			queryParams: {
				order
			}
		});
	}

	ionViewDidEnter() {
		console.log(this.verifiedOrder);
		this.repository.order.emit(null);
	}

	async onInitFinish() {
		super.onInitFinish();
		this.manualUpdateOrder();
		this.route.queryParams.subscribe(params => {
			console.log({
				params
			});
			if (this.verifiedOrder && this.verifiedOrder._id && !params.order) {
				this.orderId = this.verifiedOrder._id;
			} else {
				this.orderId = params.order;
			}
			this.reloadOrder();
		});
	}

	async reloadOrder(attempt: number = 0, error = null) {
		this.error = null;
		this.loading = true;
		if (attempt > 10) {
			this.error = error ? error : this.translate.instant('payment_success_page.payment_error');
			this.snackbarCtrl.open(error, null, {
				duration: 2000
			});
			this.loading = false;
			return;
		}
		if (attempt > 0) {
			await sleep(200);
		}
		try {
			const orderResponse = await Api.getOrder(this.orderId);
			this.repository.verifiedOrder.emit(orderResponse.data);
			if (!this.verifiedOrder.isPayed) {
				await this.reloadOrder(attempt + 1, this.translate.instant('payment_success_page.please_reload'));
				return;
			}

			const params = await firstOfObservable(this.route.params);
			if (params.payment) {
				const payment = (await Api.getPayment(params.payment)).data;
				this.tip = numberD(payment.tip);

				this.waitForReceipt$(params.payment)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						complete: () => {
							if (this.payment && !this.payment.posCheckoutReceipt) {
								this.openRequestEmail();
							}
						},
						error: err => {
							console.error('Polling error:', err);
						}
					});
			}
			this.analytics.paymentSuccess();
			try {
				if (this.venue && this.venue._id === this.verifiedOrder.venue) {
					this.orderVenue = this.venue;
				} else {
					this.orderVenue = (await Api.getLazyVenue(this.verifiedOrder.venue)).data;
				}
				this.error = false;
				this.loading = false;
			} catch (e) {
				console.error(e);
				await this.reloadOrder(attempt + 1, error ? error : e);
				return;
			}
		} catch (e) {
			console.error(e);
			await this.reloadOrder(attempt + 1, e);
			return;
		}
	}

	async home() {
		if (this.loading) {
			return;
		}
		if (this.error) {
			this.reloadOrder();
			return;
		}
		this.loading = true;

		if (
			this.verifiedOrder?.preorder?.type === PreorderType.INSIDE &&
			this.verifiedOrder.table &&
			this.verifiedOrder.tableNumber !== 'preorder_table'
		) {
			const venue = JSON.parse(JSON.stringify(this.orderVenue));
			const tableNumber = this.verifiedOrder.tableNumber;
			const tableId = this.verifiedOrder.table;

			await this.repository.cleanRepository();

			this.repository.venue.emit(venue);
			this.repository.createOrder(venue, null, PreorderType.INSIDE);
			this.repository._order.table = tableId;
			this.repository._order.tableNumber = tableNumber;
			this.repository.order.emit(this.repository._order);
			sleep(200);

			await MenuPage.navigate(this.router);
			return;
		}

		HomePage.navigate(this.router, this.repository);
	}

	openReviewHyperlink(venue: Venue) {
		try {
			console.log(venue);
			const url = (venue as Venue & {reviewHyperlink: string}).reviewHyperlink;

			window.open(url, '_blank');
		} catch (error) {
			console.log(error);
		}
	}

	async openRequestEmail() {
		await RequestEmailComponent.show(this.modalCtrl, this.payment, this.orderVenue);
	}

	@HostListener('window:pagehide')
	@HostListener('document:visibilitychange')
	onPageLifecycleChange() {
		if (document.visibilityState === 'visible') {
			return;
		}
		this.sendVrPaymentBeacon();
	}

	private sendVrPaymentBeacon() {
		if (this.vrPaymentConfirmed || this.beaconSent || !this.payment?._id) {
			return;
		}
		this.beaconSent = true;
		Api.patchVrPaymentBeacon(this.payment._id);
	}

	waitForReceipt$(paymentId: string) {
		const intervalMs = 10_000; // every 10 sec
		const timeoutMs = 1 * 60 * 1_000; // 1 min
		const startTime = Date.now();

		return timer(0, intervalMs).pipe(
			takeUntil(this.destroy$),
			switchMap(() => Api.getPayment(paymentId).then(res => res.data)),
			tap(payment => {
				this.payment = payment;
				this.tip = numberD(payment.tip);
			}),
			takeWhile(payment => {
				const receiptExists = !!payment.posCheckoutReceipt;
				const timeoutReached = Date.now() - startTime > timeoutMs;
				return !receiptExists && !timeoutReached;
			}, true),
			catchError(err => {
				console.error('Polling failed:', err);
				return throwError(() => new Error('Failed to retrieve receipt'));
			})
		);
	}

	generatePdf() {
		const receiptText = this.payment?.posCheckoutReceipt;

		if (!receiptText) {
			return;
		}
		pdfMake.fonts = {
			Courier: {
				normal: 'CourierPrime-Regular.ttf',
				bold: 'CourierPrime-Bold.ttf',
				italics: 'CourierPrime-Italic.ttf',
				bolditalics: 'CourierPrime-BoldItalic.ttf'
			}
		};

		const docDefinition: TDocumentDefinitions = {
			content: [
				{
					columns: [
						{
							// auto-sized columns have their widths based on their content
							width: '*',
							text: ''
						},
						{
							width: 'auto',
							text: receiptText,
							alignment: 'center'
						},
						{
							width: '*',
							text: ''
						}
					],
					fontSize: 9,
					font: 'Courier',
					lineHeight: 1
				}
			],
			pageSize: 'A5',
			pageMargins: [10, 10, 10, 10]
		};

		pdfMake.createPdf(docDefinition).open();
	}

	private async updateVrPayment(paymentId: string, attempt = 1, maxAttempts = 3): Promise<Payment> {
		const baseDelay = 4000;
		const factor = 2;

		let payment: Payment;
		try {
			payment = await Api.patchVrPayment(paymentId);
			console.log(`Attempt ${attempt}:`, payment);
		} catch (err) {
			console.error(`Attempt ${attempt} failed:`, err);
			if (attempt >= maxAttempts) {
				throw err;
			}
			await sleep(baseDelay * Math.pow(factor, attempt - 1));
			return this.updateVrPayment(paymentId, attempt + 1, maxAttempts);
		}

		if (payment.status === PaymentStatus.done || attempt >= maxAttempts) {
			return payment;
		}

		await sleep(baseDelay * Math.pow(factor, attempt - 1));
		return this.updateVrPayment(paymentId, attempt + 1, maxAttempts);
	}

	private async manualUpdateOrder() {
		const params = await firstOfObservable(this.route.params);

		if (!params.payment) {
			return;
		}

		this.payment = (await Api.getPayment(params.payment)).data;

		this.updateVrPayment(this.payment._id)
			.then(payment => {
				this.payment = payment;
				this.vrPaymentConfirmed = payment.status === PaymentStatus.done;
			})
			.catch(err => {
				console.error('payment manual update failed ' + err.message);
			});
	}
}
